import { SubscriptionPlan } from '@innovationdepartment/proxima-sdk-axios';
import { useHandleApi, useProximaSDK } from 'hooks';
import { useBrandStore } from 'stores';
import { CreateBillingContactSchema } from 'types/billing';

const useBillingApi = () => {
  const billingApi = useProximaSDK('BillingApi');
  const brandsApi = useProximaSDK('BrandsApi');
  const brandStore = useBrandStore();

  const { loading, handleApi } = useHandleApi();

  const getBillingContacts = async (brandId: string) =>
    handleApi({ endpoint: `/brand/${brandId}/billing/contact` });

  const createBillingContact = async (contact: CreateBillingContactSchema, brandId: string) =>
    handleApi({
      endpoint: `/brand/${brandId}/billing/contact`,
      method: 'POST',
      data: contact,
    });

  const deleteBillingContact = async ({
    contactId,
    brandId,
  }: {
    contactId: string;
    brandId: string;
  }) =>
    handleApi({
      endpoint: `/brand/${brandId}/billing/contact/${contactId}`,
      method: 'DELETE',
    });

  const updateSubscription = async (brandId: string, subscriptionType: string) =>
    handleApi({
      endpoint: `/brand/${brandId}/billing/subscription`,
      method: 'PUT',
      data: { subscriptionType },
      brandId,
    });

  const getBrandSubscription = async (brandId: string) =>
    handleApi({
      endpoint: `/brand/${brandId}/billing/subscription`,
      brandId,
      skipErrorToaster: true,
    });

  const getStripeCustomerPortalUrl = async (brandId: string) =>
    handleApi({ endpoint: `/brand/${brandId}/billing/stripe/customer-portal`, brandId });

  const createShopifySubscription = async (brandId: string, subscriptionType: SubscriptionPlan) => {
    const response = await billingApi.createSubscription({
      brandId,
      createSubscriptionInput: {
        plan: subscriptionType,
      },
    });

    if (response.status !== 200) return undefined;

    const { confirmationUrl } = response.data;

    return confirmationUrl;
  };

  const deleteShopifySubscription = async (brandId: string) => {
    await billingApi.cancelSubscription({ brandId });
  };

  const getShopifySubscription = async (brandId: string) => {
    try {
      /* this can throw a 404 if no subscription is found */
      const response = await billingApi.getSubscription1({ brandId }, { skipErrorToaster: true });

      return response.data;
    } catch (err) {
      /*  */
    }

    return null;
  };

  const userChargesAccepted = async (brandId: string) => {
    await billingApi.userAcceptedCharges({ brandId });
  };

  const refreshBrandData = async () => {
    const {
      brand: { brandId },
    } = brandStore;

    if (!brandId) return;

    const brandResponse = await brandsApi.getBrand({ brandId });

    if (brandResponse.status === 200) {
      const brand = brandResponse.data;
      brandStore.updateBrandStore({ isManaged: false, ...brand });
    }
  };

  return {
    getBillingContacts,
    createBillingContact,
    deleteBillingContact,
    getBrandSubscription,
    getStripeCustomerPortalUrl,
    loading: loading || billingApi.loading || brandsApi.loading,
    updateSubscription,
    /* shopify billing */
    createShopifySubscription,
    deleteShopifySubscription,
    getShopifySubscription,
    userChargesAccepted,
    refreshBrandData /* used when billing info updates brand info as well */,
  };
};

export default useBillingApi;
